import {
    useEffect,
    useId,
    useMemo,
    useRef,
    useState,
    type CSSProperties,
    type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '../../utils';

export interface AnchoredSelectOption {
    value: string;
    label: string;
    icon?: ReactNode;
    disabled?: boolean;
}

interface FloatingMenuPosition {
    left: number;
    width: number;
    maxHeight: number;
    top?: number;
    bottom?: number;
}

interface AnchoredSelectProps {
    label: string;
    value: string;
    options: readonly AnchoredSelectOption[];
    onChange: (value: string) => void;
    placeholder?: string;
    disabled?: boolean;
    error?: string;
    className?: string;
    triggerClassName?: string;
    hideLabel?: boolean;
    id?: string;
    icon?: ReactNode;
}

const VIEWPORT_MARGIN = 12;
const MENU_GAP = 8;
const MAX_MENU_HEIGHT = 320;
const MIN_USEFUL_MENU_SPACE = 180;

function calculateMenuPosition(trigger: DOMRect): FloatingMenuPosition {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const availableBelow = viewportHeight - trigger.bottom - MENU_GAP - VIEWPORT_MARGIN;
    const availableAbove = trigger.top - MENU_GAP - VIEWPORT_MARGIN;
    const openAbove = availableBelow < MIN_USEFUL_MENU_SPACE && availableAbove > availableBelow;
    const availableHeight = Math.max(120, openAbove ? availableAbove : availableBelow);
    const width = Math.min(trigger.width, viewportWidth - VIEWPORT_MARGIN * 2);
    const left = Math.min(
        Math.max(trigger.left, VIEWPORT_MARGIN),
        Math.max(VIEWPORT_MARGIN, viewportWidth - VIEWPORT_MARGIN - width),
    );

    return {
        left,
        width,
        maxHeight: Math.min(MAX_MENU_HEIGHT, availableHeight),
        ...(openAbove
            ? { bottom: viewportHeight - trigger.top + MENU_GAP }
            : { top: trigger.bottom + MENU_GAP }),
    };
}

export function AnchoredSelect({
    label,
    value,
    options,
    onChange,
    placeholder = 'Selecciona una opción',
    disabled = false,
    error,
    className = '',
    triggerClassName,
    hideLabel = false,
    id,
    icon,
}: AnchoredSelectProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [menuPosition, setMenuPosition] = useState<FloatingMenuPosition | null>(null);
    const generatedId = useId();
    const fieldId = id ?? generatedId;
    const menuId = `${fieldId}-menu`;
    const labelId = `${fieldId}-label`;
    const errorId = `${fieldId}-error`;
    const containerRef = useRef<HTMLDivElement | null>(null);
    const triggerRef = useRef<HTMLButtonElement | null>(null);
    const menuRef = useRef<HTMLDivElement | null>(null);
    const enabledOptions = useMemo(() => options.filter((option) => !option.disabled), [options]);
    const selectedOption = options.find((option) => option.value === value);
    const isDisabled = disabled || enabledOptions.length === 0;
    const isMenuOpen = isOpen && !isDisabled;

    const updatePosition = () => {
        const trigger = triggerRef.current?.getBoundingClientRect();
        if (trigger) {
            setMenuPosition(calculateMenuPosition(trigger));
        }
    };

    useEffect(() => {
        if (!isMenuOpen) {
            return;
        }

        const handlePointerDown = (event: PointerEvent) => {
            const target = event.target as Node;
            if (!containerRef.current?.contains(target) && !menuRef.current?.contains(target)) {
                setIsOpen(false);
            }
        };
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setIsOpen(false);
                triggerRef.current?.focus();
            }
        };
        const handleViewportChange = () => updatePosition();

        document.addEventListener('pointerdown', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);
        window.addEventListener('resize', handleViewportChange);
        window.addEventListener('scroll', handleViewportChange, true);

        const selectedButton = menuRef.current?.querySelector<HTMLButtonElement>('[aria-selected="true"]');
        const firstButton = menuRef.current?.querySelector<HTMLButtonElement>('[role="option"]:not(:disabled)');
        const focusTarget = selectedButton ?? firstButton;
        focusTarget?.focus({ preventScroll: true });
        if (typeof focusTarget?.scrollIntoView === 'function') {
            focusTarget.scrollIntoView({ block: 'nearest' });
        }

        return () => {
            document.removeEventListener('pointerdown', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('resize', handleViewportChange);
            window.removeEventListener('scroll', handleViewportChange, true);
        };
    }, [isMenuOpen]);

    const selectOption = (nextValue: string) => {
        onChange(nextValue);
        setIsOpen(false);
        triggerRef.current?.focus();
    };

    const toggleMenu = () => {
        if (isMenuOpen) {
            setIsOpen(false);
            return;
        }

        updatePosition();
        setIsOpen(true);
    };

    const handleMenuKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
            return;
        }

        event.preventDefault();
        const buttons = Array.from(
            menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="option"]:not(:disabled)') ?? [],
        );
        if (buttons.length === 0) {
            return;
        }

        const currentIndex = buttons.indexOf(document.activeElement as HTMLButtonElement);
        let nextIndex = currentIndex;

        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = buttons.length - 1;
        if (event.key === 'ArrowDown') nextIndex = currentIndex < 0 ? 0 : (currentIndex + 1) % buttons.length;
        if (event.key === 'ArrowUp') nextIndex = currentIndex <= 0 ? buttons.length - 1 : currentIndex - 1;

        buttons[nextIndex]?.focus();
        if (typeof buttons[nextIndex]?.scrollIntoView === 'function') {
            buttons[nextIndex].scrollIntoView({ block: 'nearest' });
        }
    };

    const floatingStyle: CSSProperties | undefined = menuPosition
        ? {
            left: menuPosition.left,
            width: menuPosition.width,
            maxHeight: menuPosition.maxHeight,
            top: menuPosition.top,
            bottom: menuPosition.bottom,
        }
        : undefined;

    return (
        <div ref={containerRef} className={cn('app-form-field flex flex-col gap-1.5', className)}>
            <span
                id={labelId}
                className={cn('text-sm font-medium text-text-secondary', hideLabel && 'sr-only')}
            >
                {label}
            </span>

            <button
                ref={triggerRef}
                id={fieldId}
                type="button"
                className={cn(
                    'flex min-h-12 w-full touch-manipulation items-center gap-3 rounded-xl border border-border bg-surface px-4 py-3 text-left text-base text-text transition-colors',
                    'focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/25',
                    'disabled:cursor-not-allowed disabled:bg-disabled-bg disabled:opacity-60',
                    error && 'border-error focus-visible:ring-error/25',
                    triggerClassName,
                )}
                onClick={toggleMenu}
                disabled={isDisabled}
                aria-labelledby={labelId}
                aria-haspopup="listbox"
                aria-expanded={isMenuOpen}
                aria-controls={menuId}
                aria-invalid={Boolean(error)}
                aria-describedby={error ? errorId : undefined}
            >
                <span className={cn('min-w-0 flex-1', selectedOption ? 'text-text' : 'text-text-muted')}>
                    <span className="flex items-center gap-2">
                        {selectedOption?.icon ? (
                            <span className="shrink-0 text-text-muted" aria-hidden="true">
                                {selectedOption.icon}
                            </span>
                        ) : null}
                        <span className="min-w-0 truncate">{selectedOption?.label ?? placeholder}</span>
                    </span>
                </span>
                {icon ? <span className="shrink-0 text-text-muted" aria-hidden="true">{icon}</span> : null}
                <ChevronDown
                    size={20}
                    className={cn('shrink-0 text-text-muted transition-transform', isMenuOpen && 'rotate-180')}
                    aria-hidden="true"
                />
            </button>

            {error ? (
                <span id={errorId} className="text-xs text-error" role="alert">
                    {error}
                </span>
            ) : null}

            {isMenuOpen && menuPosition && typeof document !== 'undefined'
                ? createPortal(
                    <div
                        ref={menuRef}
                        id={menuId}
                        role="listbox"
                        aria-labelledby={labelId}
                        className="fixed z-[1000] overflow-y-auto overscroll-contain rounded-xl border border-border bg-surface p-1.5 shadow-pop"
                        style={floatingStyle}
                        onKeyDown={handleMenuKeyDown}
                    >
                        <div className="space-y-1">
                            {options.map((option) => {
                                const isSelected = option.value === value;

                                return (
                                    <button
                                        key={option.value}
                                        type="button"
                                        role="option"
                                        aria-selected={isSelected}
                                        disabled={option.disabled}
                                        onClick={() => selectOption(option.value)}
                                        className={cn(
                                            'flex min-h-12 w-full touch-manipulation items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium transition-colors',
                                            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/45',
                                            isSelected
                                                ? 'bg-primary text-white'
                                                : 'text-text hover:bg-hover active:bg-hover',
                                            option.disabled && 'cursor-not-allowed opacity-45',
                                        )}
                                    >
                                        {option.icon ? (
                                            <span className="shrink-0" aria-hidden="true">
                                                {option.icon}
                                            </span>
                                        ) : null}
                                        <span className="min-w-0 flex-1 break-words">{option.label}</span>
                                        {isSelected ? <Check size={18} className="shrink-0" aria-hidden="true" /> : null}
                                    </button>
                                );
                            })}
                        </div>
                    </div>,
                    document.body,
                )
                : null}
        </div>
    );
}
